import { useMemo, useCallback } from "react";
import { normalizeValue, selectionToTeamEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelectedChips,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";
import { useTeamPool } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type TeamSelectorProps = Omittable<{
  value?: string | string[] | null;
  onValueChange?: (ids: string[]) => void;
  exclude?: string[];
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  chips?: boolean;
  summary?: boolean;
  icon?: IconName;
  fill?: boolean;
  contentFit?: boolean;
  contentSize?: EntitySelectorContentSize;
  children?: React.ReactNode;
}>;

export function TeamSelector({
  value,
  onValueChange,
  exclude,
  maxSelections,
  variant,
  placeholder = "Select teams...",
  size,
  disabled,
  chips,
  summary,
  icon,
  fill,
  contentFit,
  contentSize,
  children,
}: TeamSelectorProps) {
  const { pool } = useTeamPool({ exclude });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections,
      searchPlaceholder: "Search teams...",
      emptyMessage: "No teams found",
      noItemsMessage: "No teams yet",
    }),
    [pool, maxSelections],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const ids = normalizeValue(value);
    if (ids.length === 0) return [];
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return ids
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: pool.id, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onValueChange={handleValueChange}>
      {children ?? (
        <>
          {chips || summary ? (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
              chips={chips}
              summary={summary}
              fill={fill}
              icon={icon}
            />
          ) : (
            <SelectionEntityTrigger
              toEntity={selectionToTeamEntity}
              placeholder={placeholder}
              title="Teams"
              emptyIcon={icon}
              size={size}
              disabled={disabled}
              fill={fill}
            />
          )}
          <EntitySelectorContent size={contentSize} fit={contentFit}>
            <EntitySelectorInput />
            <EntitySelectorSelectedChips label="Selected" />
            <EntitySelectorPool value={pool.id} />
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
